import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { v4 } from 'uuid';
import { DB, Storage } from '~/services/firebase';
import { useAuth } from '~/hooks/auth';

import Dashboard from '~/layouts/Dashboard';
import getValidationErrors from '~/utils/getValidationErrors';

import { useToast } from '~/hooks/toast';

interface FormData {
  uid: string;
  name: string;
  phone: string;
  email: string;
  image: File;
  vehicle_plate: string;
  status: boolean;
}

interface Params {
  id: string;
}

const DeliverymenForm: React.FC = () => {
  const history = useHistory();
  const params = useParams<Params>();
  const { user, company } = useAuth();
  const { addToast } = useToast();

  const [data, setData] = useState({} as FormData);
  const [errors, setErrors] = useState({} as any);
  const [loading, setLoading] = useState(false);
  const { id } = params;

  useEffect(() => {
    async function load() {
      const docSnap = await getDoc(doc(DB, 'deliverymen', id));
      setData({
        ...docSnap.data(),
        uid: id,
      } as FormData);
    }

    if (id) load();
    else
      setData({
        status: true,
        phone: '',
        email: '',
        vehicle_plate: '',
      } as FormData);
  }, [id]);

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      try {
        const schema = Yup.object().shape({
          name: Yup.string().required('O nome é obrigatório'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        setLoading(true);

        const { name, status, phone, email, vehicle_plate } = data;

        const inputFile = document.getElementById('photo') as HTMLInputElement;

        let image = '';

        if (inputFile && inputFile.files && inputFile.files.length > 0) {
          const file = inputFile.files[0];
          const storageRef = Storage.ref(`deliverymen/${v4()}.png`);
          const resposne = await storageRef.put(file);
          image = await resposne.ref.getDownloadURL();
        }

        const uid = data.uid || v4();

        let parseData: any = {
          name,
          user_id: user.uid,
          company_id: company.uid,
          status,
          phone,
          email,
          vehicle_plate,
        };

        if (image) parseData = { image, ...parseData };

        if (data.uid) {
          await updateDoc(doc(DB, 'deliverymen', uid), parseData);
        } else {
          await setDoc(doc(DB, 'deliverymen', uid), parseData);
        }

        addToast({
          type: 'success',
          description: data.uid
            ? 'Entregador alterado com sucesso!'
            : 'Entregador criado com sucesso!',
        });

        history.push('/deliverymen');

        setLoading(false);
      } catch (err: any) {
        console.log(err);
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const parseErrors = getValidationErrors(err);
          setErrors(parseErrors);
        }
      }
    },
    [user, company, data, addToast, history],
  );

  return (
    <Dashboard containerMessage="col-xl-6 offset-xl-3 col-md-10 offset-md-1">
      <div className="row">
        <div className="col-xl-6 offset-xl-3 col-md-10 offset-md-1">
          <div className="nk-block nk-block-lg">
            <div className="nk-block-head">
              <div className="nk-block-head-content">
                <div className="nk-block-head-sub">
                  <Link className="back-to" to="/deliverymen">
                    <em className="icon ni ni-arrow-left" />
                    <span>Voltar</span>
                  </Link>
                </div>
                <h4 className="title nk-block-title">
                  {id ? 'Editar Entregador' : 'Cadastrar Entregador'}
                </h4>
                <div className="nk-block-des">
                  <p>
                    Preencha o formulário abaixo e cadastre um novo entregador.
                  </p>
                </div>
              </div>
            </div>
            <div className="card card-bordered">
              <div className="card-inner">
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Nome</label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={data.name}
                            onChange={event => {
                              setData({ ...data, name: event.target.value });
                            }}
                          />
                          {errors.name && (
                            <span className="invalid">{errors.name}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Contato</label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={data.phone}
                            onChange={event => {
                              setData({ ...data, phone: event.target.value });
                            }}
                          />
                          {errors.phone && (
                            <span className="invalid">{errors.phone}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="form-group mb-3">
                        <label className="form-label">E-mail</label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={data.email}
                            onChange={event => {
                              setData({ ...data, email: event.target.value });
                            }}
                          />
                          {errors.email && (
                            <span className="invalid">{errors.email}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Placa do Veículo</label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={data.vehicle_plate}
                            onChange={event => {
                              setData({
                                ...data,
                                vehicle_plate: event.target.value,
                              });
                            }}
                          />
                          {errors.vehicle_plate && (
                            <span className="invalid">
                              {errors.vehicle_plate}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* <div className="col-lg-12">
                      <div className="form-group mb-3">
                        <label className="form-label">Imagem</label>
                        <div className="form-control-wrap">
                          <input
                            type="file"
                            id="photo"
                            style={{ width: '100%' }}
                          />
                        </div>
                      </div>
                    </div> */}

                    <div className="col-lg-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Status?</label>
                        <ul className="custom-control-group g-3 align-center">
                          <li>
                            <div className="custom-control custom-control-sm custom-checkbox">
                              <input
                                id="active"
                                type="checkbox"
                                className="custom-control-input"
                                defaultChecked={data.status}
                                onClick={() => {
                                  setData({
                                    ...data,
                                    status: !data.status,
                                  });
                                }}
                              />
                              <label
                                className="custom-control-label"
                                htmlFor="active"
                              >
                                Ativo
                              </label>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group">
                        <button
                          type="submit"
                          className="btn btn-lg btn-primary"
                          disabled={loading}
                        >
                          {loading && (
                            <div
                              className="spinner-border spinner-border-sm"
                              role="status"
                            />
                          )}
                          {!loading && 'Salvar'}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dashboard>
  );
};

export default DeliverymenForm;
